import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ChatbotMessage } from 'src/app/interfaces/chatbot-message.interfaces';
import { ChatbotSession } from 'src/app/interfaces/chatbot-session.interfaces';
import { ConnectionStatus } from './chatbot-websocket.service';

/**
 * Servicio de estado centralizado para el módulo de chatbot.
 *
 * Gestiona el estado reactivo de la interfaz mediante BehaviorSubjects,
 * exponiendo Observables públicos para que los componentes se suscriban
 * sin acceso directo a los subjects internos.
 *
 * Estado gestionado:
 *   - Estado de conexión WebSocket (conectado / desconectado / reconectando)
 *   - Sesión activa seleccionada por el usuario
 *   - Lista de mensajes de la sesión activa
 *   - Estado de carga (esperando el primer token de respuesta)
 *   - Lista de sesiones del usuario
 *
 * Requisitos: 5.4, 5.5, 5.6, 4.2, 4.3
 */
@Injectable({
  providedIn: 'root'
})
export class ChatbotStateService {

  // ---------------------------------------------------------------------------
  // Estado de conexión WebSocket
  // ---------------------------------------------------------------------------

  private readonly _connectionStatus$ = new BehaviorSubject<ConnectionStatus>('disconnected');

  /**
   * Observable del estado de conexión WebSocket.
   * Valores posibles: 'connected' | 'disconnected' | 'reconnecting'
   */
  readonly connectionStatus$: Observable<ConnectionStatus> = this._connectionStatus$.asObservable();

  // ---------------------------------------------------------------------------
  // Sesión activa
  // ---------------------------------------------------------------------------

  private readonly _activeSession$ = new BehaviorSubject<ChatbotSession | null>(null);

  /**
   * Observable de la sesión de conversación actualmente seleccionada.
   * Emite `null` cuando no hay ninguna sesión activa.
   */
  readonly activeSession$: Observable<ChatbotSession | null> = this._activeSession$.asObservable();

  // ---------------------------------------------------------------------------
  // Mensajes de la sesión activa
  // ---------------------------------------------------------------------------

  private readonly _messages$ = new BehaviorSubject<ChatbotMessage[]>([]);

  /**
   * Observable del arreglo de mensajes de la sesión activa.
   * Emite un arreglo vacío cuando no hay mensajes cargados.
   */
  readonly messages$: Observable<ChatbotMessage[]> = this._messages$.asObservable();

  // ---------------------------------------------------------------------------
  // Estado de carga
  // ---------------------------------------------------------------------------

  private readonly _isLoading$ = new BehaviorSubject<boolean>(false);

  /**
   * Observable del estado de carga.
   * Emite `true` mientras se espera el primer token de respuesta del LLM.
   * Emite `false` cuando llega el primer token o cuando la respuesta finaliza.
   */
  readonly isLoading$: Observable<boolean> = this._isLoading$.asObservable();

  // ---------------------------------------------------------------------------
  // Lista de sesiones
  // ---------------------------------------------------------------------------

  private readonly _sessions$ = new BehaviorSubject<ChatbotSession[]>([]);

  /**
   * Observable del listado de sesiones de conversación del usuario.
   * Emite un arreglo vacío hasta que se carguen las sesiones desde el backend.
   */
  readonly sessions$: Observable<ChatbotSession[]> = this._sessions$.asObservable();

  // ---------------------------------------------------------------------------
  // Getters síncronos (valores actuales)
  // ---------------------------------------------------------------------------

  /** Devuelve el estado de conexión actual de forma síncrona. */
  get connectionStatus(): ConnectionStatus {
    return this._connectionStatus$.value;
  }

  /** Devuelve la sesión activa actual de forma síncrona. */
  get activeSession(): ChatbotSession | null {
    return this._activeSession$.value;
  }

  /** Devuelve el arreglo de mensajes actual de forma síncrona. */
  get messages(): ChatbotMessage[] {
    return this._messages$.value;
  }

  /** Devuelve el estado de carga actual de forma síncrona. */
  get isLoading(): boolean {
    return this._isLoading$.value;
  }

  /** Devuelve la lista de sesiones actual de forma síncrona. */
  get sessions(): ChatbotSession[] {
    return this._sessions$.value;
  }

  // ---------------------------------------------------------------------------
  // Métodos de actualización de estado
  // ---------------------------------------------------------------------------

  /**
   * Actualiza el estado de conexión WebSocket.
   *
   * @param status  Nuevo estado de conexión.
   */
  setConnectionStatus(status: ConnectionStatus): void {
    this._connectionStatus$.next(status);
  }

  /**
   * Establece la sesión activa y limpia los mensajes cargados previamente.
   * Pasar `null` cierra la sesión activa sin seleccionar otra.
   *
   * @param session  Sesión a activar, o `null` para deseleccionar.
   */
  setActiveSession(session: ChatbotSession | null): void {
    this._activeSession$.next(session);
    // Al cambiar de sesión se limpian los mensajes para evitar mostrar
    // mensajes de una sesión anterior mientras se cargan los nuevos.
    this._messages$.next([]);
  }

  /**
   * Reemplaza el arreglo completo de mensajes de la sesión activa.
   * Útil al cargar el historial de una sesión desde el backend.
   *
   * @param messages  Arreglo de mensajes a establecer.
   */
  setMessages(messages: ChatbotMessage[]): void {
    this._messages$.next(messages);
  }

  /**
   * Agrega un único mensaje al final del arreglo de mensajes actual.
   *
   * @param message  Mensaje a agregar.
   */
  addMessage(message: ChatbotMessage): void {
    this._messages$.next([...this._messages$.value, message]);
  }

  /**
   * Actualiza el contenido del último mensaje del arreglo (mensaje del asistente
   * en construcción durante el streaming de tokens).
   *
   * Si el arreglo está vacío, la operación no tiene efecto.
   *
   * @param content  Contenido actualizado del último mensaje.
   */
  updateLastMessageContent(content: string): void {
    const current = this._messages$.value;
    if (current.length === 0) {
      return;
    }
    const updated = [...current];
    updated[updated.length - 1] = { ...updated[updated.length - 1], content };
    this._messages$.next(updated);
  }

  /**
   * Establece el estado de carga.
   *
   * @param loading  `true` para indicar que se espera el primer token; `false` cuando llega.
   */
  setLoading(loading: boolean): void {
    this._isLoading$.next(loading);
  }

  /**
   * Reemplaza el listado completo de sesiones del usuario.
   *
   * @param sessions  Arreglo de sesiones a establecer.
   */
  setSessions(sessions: ChatbotSession[]): void {
    this._sessions$.next(sessions);
  }

  /**
   * Agrega una nueva sesión al inicio del listado (las sesiones más recientes
   * se muestran primero).
   *
   * @param session  Sesión a agregar.
   */
  prependSession(session: ChatbotSession): void {
    this._sessions$.next([session, ...this._sessions$.value]);
  }

  /**
   * Actualiza una sesión existente en el listado (por ejemplo, al cambiar su título
   * o su fecha de última actualización).
   *
   * Si la sesión no existe en el listado, la operación no tiene efecto.
   *
   * @param updatedSession  Sesión con los datos actualizados.
   */
  updateSession(updatedSession: ChatbotSession): void {
    const updated = this._sessions$.value.map(s =>
      s.session_id === updatedSession.session_id ? updatedSession : s
    );
    this._sessions$.next(updated);
  }

  /**
   * Restablece todo el estado del servicio a sus valores iniciales.
   * Útil al cerrar sesión o al desmontar el módulo de chatbot.
   */
  reset(): void {
    this._connectionStatus$.next('disconnected');
    this._activeSession$.next(null);
    this._messages$.next([]);
    this._isLoading$.next(false);
    this._sessions$.next([]);
  }
}
